/**
 * Панель вкладки «Заметки»: шапка, черновик, список карточек.
 *
 * ЗАГОЛОВКА «ЗАМЕТКИ» ВНУТРИ НЕТ. Вкладка называет содержимое сама, и повторить её ярлык
 * дословно первой строкой панели — значит потратить строку на то, что человек только что
 * прочитал и по чему только что нажал.
 *
 * ДВЕ КНОПКИ ШАПКИ — ДВА РАЗНЫХ ПОРЯДКА РАБОТЫ, а не одно действие с вариантами. «Новая
 * заметка» — слова вперёд: черновик, в который пишут и к которому потом прикладывают кадры.
 * «Добавить фото» — файлы вперёд: системный выбор сразу, а куда они лягут, решает сервер по
 * координатам. Обе нейтральные: охра принадлежит анализу.
 *
 * ОБЕ НАЖИМАЮТСЯ НА ПОТОЛКЕ. Погашенная кнопка с ненадёжным `title` не объясняет ничего;
 * нажатие зарабатывает строку, которая называет предел числом и говорит, что делать.
 *
 * ШАПОЧНАЯ «ДОБАВИТЬ ФОТО» НЕ ЗАВОДИТ ЗАМЕТКУ ЗАРАНЕЕ. Файлы уезжают без `marker_id`, и
 * сервер раскладывает их по GPS: в заметку в 25 м, в новую заметку с местом или — если места
 * у файла нет вовсе — в ОДНУ заметку поездки, id которой очередь протаскивает через всю пачку
 * (`fallback_marker_id`, useWorkspace). Карточки появляются после перечита, поэтому до их
 * появления очередь стоит здесь, под шапкой, а не в пустоте.
 */

import { useEffect, useMemo, useState } from "react";

import "./FieldNotes.css";

import type { FieldMarker, FieldPhoto } from "../api/types";
import { isFileDrag } from "../map/drop";
import { photosOfNote, sortNotes, useNoteThumbs } from "../lib/notes";
import { todayIso } from "../lib/format";
import { T } from "../strings";
import {
  placementLine,
  type MarkerOutcome,
  type PhotosState,
  type PhotoUpload,
} from "../state/useWorkspace";
import type { NotesUiApi } from "../state/useNotesUi";
import { NoteCard } from "./NoteCard";

/** Успешная очередь гаснет через это же время, что и «Сохранено» в ростере. */
const SAVED_NOTICE_MS = 4000;

/** Со скольких заметок С МЕСТОМ кнопка «Показать все на карте» отвечает на вопрос «где они». */
const SHOW_ALL_MIN = 2;

/** Всё, что панели нужно знать о заметках поля. Один проп вместо трёх десятков у консоли. */
export interface NotesPanel {
  fieldId: number;
  notes: FieldMarker[];
  /** Фотографии ВСЕГО поля: панель сама разложит их по заметкам. */
  photos: FieldPhoto[];
  /**
   * Как прошли ОБА списка — заметок и фотографий.
   *
   * Два, а не один: упасть может любой из запросов, и панель, знающая только про фотографии,
   * печатала «Заметок нет» на упавшем `GET /markers` — то есть утверждала отсутствие там, где
   * мы не спросили. §6.3 требует одного ответа на оба: отказ с «Ещё раз», перечитывающим оба.
   */
  state: PhotosState | undefined;
  notesState: PhotosState | undefined;
  notesAtLimit: boolean;
  notesLimit: number;
  /** Потолок ФОТОГРАФИЙ на поле. Свой предел и своя строка: заметки тут ни при чём. */
  photosAtLimit: boolean;
  photoLimit: number;
  /** Потолок фотографий НА ЗАМЕТКУ (`note_limit` с сервера): им гасится приём в карточке. */
  noteLimit: number;
  uploads: PhotoUpload[];
  /** Поднятое над вкладкой состояние: черновик, редактор, раскрытые, режим постановки. */
  ui: NotesUiApi;
  onUpload: (files: File[], markerId?: number) => void;
  onRetryUpload: (uploadId: number) => void;
  onDismissUploads: () => void;
  onReload: () => void;
  onDeleteNote: (noteId: number) => Promise<MarkerOutcome>;
  /** Открыть модалку: с первого кадра (`null`) или с названного. */
  onOpen: (noteId: number, photoId: number | null) => void;
  /** Открыть модалку сразу в правке — это карандаш карточки. */
  onEdit: (noteId: number) => void;
  /** Взвести на карте режим «укажите место этой заметке». */
  onPlace: (noteId: number) => void;
  onShowOnMap: (noteId: number) => void;
  onShowAllOnMap: () => void;
  thumbUrl?: (photoId: number) => Promise<string>;
  /** Какая карточка вспыхивает: вторая половина ответа на удачную постановку места. */
  flashNoteId: number | null;
}

export function FieldNotes({ panel }: { panel: NotesPanel }) {
  const {
    fieldId,
    notes: unsorted,
    photos,
    state,
    notesState,
    notesAtLimit,
    notesLimit,
    photosAtLimit,
    photoLimit,
    noteLimit,
    uploads,
    ui,
    onUpload,
    onRetryUpload,
    onDismissUploads,
    onReload,
    onDeleteNote,
    onOpen,
    onEdit,
    onPlace,
    onShowOnMap,
    onShowAllOnMap,
    thumbUrl,
    flashNoteId,
  } = panel;

  const [limitAsked, setLimitAsked] = useState(false);
  const [over, setOver] = useState(false);

  const notes = useMemo(() => sortNotes(unsorted), [unsorted]);
  const { urls, watch } = useNoteThumbs(thumbUrl, fieldId);

  // Грузится, пока грузится ХОТЬ ОДИН: список заметок без их фотографий — это половина ленты.
  const loading = state?.phase === "loading" || notesState?.phase === "loading";
  // Отказ любого из двух. Заметки первыми: без них панели нет вовсе, а без фотографий она
  // всего лишь неполна.
  const loadError =
    (notesState?.phase === "error" ? notesState.error : null) ??
    (state?.phase === "error" ? state.error : null);
  const running = uploads.some((row) => row.status === "queued" || row.status === "uploading");
  const refused = uploads.filter((row) => row.status === "error");

  /**
   * Успешная очередь убирается сама; очередь с отказом стоит до «Закрыть».
   *
   * Список из пяти отказов нельзя прочитать за четыре секунды, а прочитать его — единственный
   * способ понять, какие файлы не поехали.
   */
  useEffect(() => {
    if (uploads.length === 0 || running || refused.length > 0) return;
    const timer = window.setTimeout(onDismissUploads, SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [uploads.length, running, refused.length, onDismissUploads]);

  // Потолок мог освободиться, пока строка стояла: удалили заметку — вопрос снят.
  useEffect(() => {
    if (!notesAtLimit) setLimitAsked(false);
  }, [notesAtLimit]);

  const take = (files: FileList | null): void => {
    const chosen = [...(files ?? [])];
    if (chosen.length > 0) onUpload(chosen);
  };

  /** Обе кнопки шапки упираются в один и тот же потолок и отвечают одной и той же строкой. */
  const atCeiling = (): boolean => {
    if (!notesAtLimit) return false;
    setLimitAsked(true);
    return true;
  };

  /**
   * «НОВАЯ ЗАМЕТКА» ОТКРЫВАЕТ МОДАЛКУ СОЗДАНИЯ (notes-v4). Черновика-карточки в списке больше
   * нет: он был ЧЕТВЁРТЫМ редактором заметки — со своим полем даты и своей текстареей, —
   * отличавшимся от модалки только тем, что у него ещё нет id. Создание и правка это одна
   * форма, и живёт она в одном месте.
   */
  const startDraft = (): void => {
    if (atCeiling()) return;
    // Дата по умолчанию — сегодня: заметку заводят в тот же день, когда вернулись с поля.
    ui.setDraft({ text: "", files: [], date: todayIso() });
  };

  const placesOnMap = notes.filter((note) => note.lon !== null && note.lat !== null).length;

  const head = (
    <div className="k-notes__head">
      <div className="k-row k-row--wrap">
        <button
          type="button"
          className="k-btn k-btn--compact"
          data-testid="notes-add"
          onClick={startDraft}
        >
          {T.markers.addNote}
        </button>
        {/* Только когда место есть МИНИМУМ у двух: «показать все» на одной заметке отвечает
            не на тот вопрос — для неё есть «Показать на карте» в самой карточке. */}
        {placesOnMap >= SHOW_ALL_MIN && (
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--compact"
            data-testid="notes-show-all"
            onClick={onShowAllOnMap}
          >
            {T.markers.showAllOnMap}
          </button>
        )}
      </div>

      {notesAtLimit && limitAsked && (
        <p className="k-hint" role="alert" data-testid="notes-limit-hint">
          {T.markers.limit(Number.isFinite(notesLimit) ? notesLimit : notes.length)}
        </p>
      )}

      {/* ВТОРОЙ ПОТОЛОК — ФОТОГРАФИЙ НА ПОЛЕ, и он стоит постоянно, а не по нажатию: заметку
          на полном поле завести ещё можно, а вот приложить к ней кадр уже нет, и узнать об
          этом из отказа каждого файла пачки значит узнать двадцать раз подряд. */}
      {photosAtLimit && (
        <p className="k-hint" data-testid="notes-photo-limit">
          {T.photos.limitHint(Number.isFinite(photoLimit) ? photoLimit : photos.length)}
        </p>
      )}
    </div>
  );

  /** Очередь ПАЧКИ — той, что уехала без заметки: её карточек ещё нет, а отчёт нужен. */
  const loose = uploads.filter((row) => row.markerId === undefined);

  const queue = loose.length > 0 && (
    <div className="k-notes__queue" data-testid="notes-queue">
      <div className="k-notes__queuehead">
        <span className="k-hint">
          {running
            ? T.photos.queue(loose.filter((row) => row.status === "done").length, loose.length)
            : T.photos.queueDone}
        </span>
        {!running && (
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--compact"
            onClick={onDismissUploads}
          >
            {T.common.close}
          </button>
        )}
      </div>
      <div className="k-queue">
        {loose.map((row) => (
          <div className="k-queuerow" key={row.id}>
            <span className="k-notecard__filename" title={row.file.name}>
              {row.file.name}
            </span>
            {row.status === "error" ? (
              <button
                type="button"
                className="k-btn k-btn--compact"
                onClick={() => onRetryUpload(row.id)}
              >
                {T.common.retry}
              </button>
            ) : (
              <span className="k-queuerow__status">
                {row.status === "done" ? T.photos.queueDone : T.common.loading}
              </span>
            )}
            {row.error !== null && (
              <p className="k-note k-note--error k-notecard__rowerror" role="alert">
                {row.error}
              </p>
            )}
            {/* ИСХОД МЕСТА, а не отказ: файл принят и лежит в заметке — сказано только, в
                какой и почему. Молчит на `explicit`: заметку назвал сам человек. */}
            {row.status === "done" && placementLine(row.placement, row.cause) !== null && (
              <p className="k-hint k-notecard__rowplace">
                {placementLine(row.placement, row.cause)}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  /**
   * ПУСТАЯ ПАНЕЛЬ — ЭТО ЗОНА ДРОПА СО СЛОВАМИ, а не экран с кнопками.
   *
   * КНОПОК ЗДЕСЬ НЕТ, И ЭТО ГЛАВНОЕ. Они стоят в шапке — и стоят там всегда; вторая пара в
   * пустышке означала бы, что орган переезжает от собственного нажатия: завёл первую заметку —
   * и кнопка, по которой только что попал, уехала наверх, а палец остался на пустом месте.
   *
   * ВЫСОТУ ДЕРЖИТ ПУНКТИРНАЯ РАМКА, а не правило «пустое состояние во всю высоту»: рамка и
   * есть цель для файла, и цель размером в панель промахнуться трудно. Слова стоят сверху, под
   * кнопками шапки, чтобы читать их не приходилось из середины пустого прямоугольника.
   */
  const empty = notes.length === 0 && !loading && loadError === null && ui.draft === null && (
    <div className="k-notes__empty" data-testid="notes-empty">
      <span className="k-empty__title">{T.markers.panelEmpty}</span>
      <p className="k-hint">{T.markers.emptyWhy}</p>
      {/* Рамка без подписи — загадка: она выглядит местом, куда что-то полагается, но чем её
          кормить, знает только тот, кто уже пробовал. */}
      <p className="k-hint" data-testid="notes-drop-hint">
        {T.markers.dropHint}
      </p>
    </div>
  );

  return (
    <div
      className="k-notes"
      data-over={over || undefined}
      data-testid="notes-panel"
      /* ЗОНА ДРОПА — ВСЯ ПАНЕЛЬ. Дроп на карту занят контурами (map/drop.ts), а фотографию
         естественнее всего уронить туда, где на неё смотрят. Файл, брошенный на панель, идёт
         тем же путём, что и кнопка шапки: сервер разложит его по координатам. */
      onDragOver={(event) => {
        if (!isFileDrag(event.dataTransfer)) return;
        // Отменять обязательно: иначе браузер уходит на файл и приложения больше нет.
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setOver(true);
      }}
      onDragLeave={(event) => {
        // `relatedTarget` внутри зоны — переход на потомка, а не уход из неё.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setOver(false);
      }}
      onDrop={(event) => {
        if (!isFileDrag(event.dataTransfer)) return;
        event.preventDefault();
        setOver(false);
        take(event.dataTransfer.files);
      }}
    >
      <input
        className="k-notes__file"
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        data-testid="notes-input"
        onChange={(event) => {
          take(event.target.files);
          event.target.value = "";
        }}
      />

      {head}

      {loadError !== null && (
        <div className="k-note k-note--error" role="alert" data-testid="notes-load-error">
          <p>{loadError}</p>
          <button type="button" className="k-btn k-btn--compact" onClick={onReload}>
            {T.common.retry}
          </button>
        </div>
      )}

      {queue}

      {loading && notes.length === 0 && <p className="k-caption">{T.common.loading}</p>}

      {empty}

      {notes.map((note) => {
        const mine = photosOfNote(photos, note.id);
        return (
          <NoteCard
            key={note.id}
            note={note}
            photos={mine}
            placing={ui.placingNoteId === note.id}
            flashing={flashNoteId === note.id}
            uploads={uploads.filter((row) => row.markerId === note.id)}
            onRetryUpload={onRetryUpload}
            noteLimit={noteLimit}
            thumbs={urls}
            watch={watch(note.id, mine)}
            onOpen={(photoId) => onOpen(note.id, photoId)}
            onEdit={() => onEdit(note.id)}
            onDelete={() => onDeleteNote(note.id)}
            onAddPhotos={(files) => onUpload(files, note.id)}
            onPlace={() => onPlace(note.id)}
            onShowOnMap={() => onShowOnMap(note.id)}
          />
        );
      })}
    </div>
  );
}
